import type { RouteLocationRaw } from "vue-router"
import safeJsonParse from "@/utils/safeParse"
import { safeSessionStorage } from "@/utils/safeStorage"

export const INITIAL_REDIRECT_SESSION_KEY = "initialRedirectInfo"
export interface InitialRedirectInfo {
  eventType?: string
  eventId?: string
  marketId?: string
}

export const parseFromUrl = (): InitialRedirectInfo => {
  const currentUrl = window.location.href
  const url = new URL(currentUrl)

  return {
    eventType: url.searchParams.get("eventType") || undefined,
    eventId: url.searchParams.get("eventId") || undefined,
    marketId: url.searchParams.get("marketId") || undefined
  }
}

export const removeParseFromUrl = () => {
  const url = new URL(window.location.href)
  url.searchParams.delete("eventType")
  url.searchParams.delete("eventId")
  url.searchParams.delete("marketId")
  window.history.replaceState({}, document.title, url.toString())
}

const hasRedirectTarget = (info: InitialRedirectInfo): boolean =>
  Boolean(info.eventType || info.eventId || info.marketId)

const isPageReload = (): boolean => {
  if (!("PerformanceNavigationTiming" in window)) return false

  const [navigation] = performance.getEntriesByType("navigation")
  return navigation instanceof PerformanceNavigationTiming && navigation.type === "reload"
}

/**
 * The URL params are stripped right after parsing, so a reload arrives without them.
 * Keep the stored target on reload (e.g. refreshing mid-Terms) and replace it on any
 * other entry, so a target left by an abandoned launch never leaks into a new one.
 */
export const saveInitialRedirect = (info: InitialRedirectInfo) => {
  if (isPageReload() && !hasRedirectTarget(info)) return

  safeSessionStorage.setItem(INITIAL_REDIRECT_SESSION_KEY, JSON.stringify(info))
}

export const clearInitialRedirect = () => {
  safeSessionStorage.removeItem(INITIAL_REDIRECT_SESSION_KEY)
}

export const resolveInitialRedirect = (
  info: InitialRedirectInfo,
  { isRacingWebSite }: { isRacingWebSite: boolean }
): RouteLocationRaw | undefined => {
  if (isRacingWebSite) return undefined
  if (info.eventType && info.eventId && info.marketId) {
    return {
      name: "FullMarket",
      params: { eventType: info.eventType, eventId: info.eventId },
      query: { marketId: info.marketId }
    }
  }
  if (info.eventType) return { name: "Highlight", query: { eventType: info.eventType } }
  return undefined
}

/** Reads the target saved at entry and removes it, so it is applied at most once. */
export const consumeInitialRedirect = ({
  isRacingWebSite
}: {
  isRacingWebSite: boolean
}): RouteLocationRaw | undefined => {
  const info = safeJsonParse<InitialRedirectInfo>(
    safeSessionStorage.getItem(INITIAL_REDIRECT_SESSION_KEY),
    {}
  )
  clearInitialRedirect()

  return resolveInitialRedirect(info, { isRacingWebSite })
}

export default {
  parseFromUrl,
  removeParseFromUrl,
  INITIAL_REDIRECT_SESSION_KEY
}
